import { useCallback, useEffect, useMemo, useState } from 'react';

import { calculateButtonWidth } from '../helpers/calculate-button-width';
import { handleOverflowOfButtons } from '../helpers/handle-overflow-of-buttons';

import type {
  UseContentSwitcherOverflowProps,
  UseContentSwitcherOverflowResult,
} from './use-handle-content-switcher.types';

export const useHandleContentSwitcherOverflow = ({
  allowDropdown,
  data,
  containerRef,
  size,
}: UseContentSwitcherOverflowProps): UseContentSwitcherOverflowResult => {
  const [containerWidth, setContainerWidth] = useState(0);

  // Memoize the width of all Buttons
  const buttonWidths: number[] = useMemo(
    () =>
      !allowDropdown
        ? []
        : data.map((item) =>
            calculateButtonWidth({
              copy: item.copy,
              size,
              hasIcon: !!item.icon,
              hasMiniBadge: !!item.miniBadge,
            })
          ),
    [allowDropdown, data, size]
  );

  // Result of the use-handle-content-switcher-overflow
  const { visibleButtons, overflowedButtons } = useMemo(
    (): UseContentSwitcherOverflowResult =>
      !allowDropdown
        ? { visibleButtons: data.map((_, i) => i), overflowedButtons: [] }
        : handleOverflowOfButtons(buttonWidths, containerWidth),
    [data, allowDropdown, buttonWidths, containerWidth]
  );

  const updateContainerWidth = useCallback((): void => {
    if (!allowDropdown || !containerRef.current) return;
    setContainerWidth(containerRef.current.getBoundingClientRect().width);
  }, [allowDropdown, containerRef]);

  // Update the container width when the window is resized, or when the containerRef.current changes
  useEffect(() => {
    if (!allowDropdown || !containerRef.current) return;

    updateContainerWidth();

    window.addEventListener('resize', updateContainerWidth);
    const resizeObserver = new ResizeObserver(updateContainerWidth);
    resizeObserver.observe(containerRef.current);

    return () => {
      window.removeEventListener('resize', updateContainerWidth);
      resizeObserver.disconnect();
    };
  }, [allowDropdown, containerRef, updateContainerWidth]);

  return { visibleButtons, overflowedButtons };
};
